import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import StatusIndicator from '../../components/ui/StatusIndicator';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import AdminNavigation from '../../components/ui/AdminNavigation';

const AdminShipmentDetailView = () => {
  const { shipmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shipment, setShipment] = useState(null);
  const [error, setError] = useState(null);
  const [shipmentItems, setShipmentItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (shipmentId) {
      fetchShipmentDetails();
    }
  }, [shipmentId]);

  const fetchShipmentDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch shipment data by ID
      const { data: shipmentData, error: shipmentError } = await supabase?.from('shipments')?.select('*')?.eq('id', shipmentId)?.single();

      if (shipmentError) throw shipmentError;

      if (!shipmentData) {
        setError('Aucun envoi trouvé pour cet identifiant');
        setLoading(false);
        return;
      }

      setShipment(shipmentData);

      // Fetch shipment items
      const { data: itemsData, error: itemsError } = await supabase?.from('shipment_items')?.select('*, catalog_items(*)')?.eq('shipment_id', shipmentId);

      if (itemsError) throw itemsError;
      setShipmentItems(itemsData || []);

      // Fetch events
      const { data: eventsData, error: eventsError } = await supabase?.from('shipment_events')?.select('*')?.eq('shipment_id', shipmentId)?.order('created_at', { ascending: false });

      if (eventsError) throw eventsError;
      setEvents(eventsData || []);

      // Fetch documents
      const { data: documentsData, error: documentsError } = await supabase?.from('shipment_files')?.select('*')?.eq('shipment_id', shipmentId)?.eq('file_type', 'document');

      if (documentsError) throw documentsError;
      setDocuments(documentsData || []);

      setLoading(false);
    } catch (err) {
      console.error('Error fetching shipment details:', err);
      setError(err?.message || 'Erreur lors du chargement des détails');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem('adtAdminAuth');
      await supabase?.auth?.signOut();
      navigate('/admin-login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('adtAdminAuth');
      navigate('/admin-login', { replace: true });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'En attente',
      'in_transit': 'En transit',
      'customs': 'En douane',
      'out_for_delivery': 'En livraison',
      'delivered': 'Livré',
      'exception': 'Exception'
    };
    return labels?.[status] || status;
  };

  const getStatusKey = (status) => {
    const statusMap = {
      'pending': 'pending',
      'in_transit': 'in-transit',
      'customs': 'processing',
      'out_for_delivery': 'in-transit',
      'delivered': 'delivered',
      'exception': 'error'
    };
    return statusMap?.[status] || 'pending';
  };

  const breadcrumbs = [
    { label: 'Dashboard', path: '/admin-shipments-dashboard' },
    { label: shipment?.reference || 'Chargement...', path: null }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation onLogout={handleLogout} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Icon name="Loader" className="animate-spin mx-auto mb-4 text-primary" size={48} />
            <p className="text-muted-foreground">Chargement des détails de l'envoi...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation onLogout={handleLogout} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <Icon name="AlertTriangle" className="mx-auto mb-4 text-destructive" size={48} />
            <h2 className="text-2xl font-bold text-foreground mb-2">Erreur</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <div className="space-x-2">
              <Button onClick={() => navigate('/admin-shipments-dashboard')}>
                Retour à la liste des envois
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation onLogout={handleLogout} />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <BreadcrumbTrail customItems={breadcrumbs} />

        <div className="mt-6 space-y-6">
          {/* Header with actions */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Détails de l'envoi {shipment?.reference}
              </h1>
              <div className="mt-2">
                <StatusIndicator 
                  status={getStatusKey(shipment?.current_status)} 
                  label={getStatusLabel(shipment?.current_status)}
                  size="lg"
                />
              </div>
            </div>
            <div className="flex space-x-2">
              <Button 
                onClick={() => navigate(`/admin/shipments/${shipmentId}`)} 
                variant="default"
              >
                <Icon name="Edit" size={18} />
                Modifier l'envoi
              </Button>
              <Button onClick={() => navigate('/admin-shipments-dashboard')} variant="outline">
                <Icon name="ArrowLeft" size={18} />
                Retour
              </Button>
            </div>
          </div>

          {/* Shipment Information Card */}
          <div className="bg-card rounded-lg border border-border p-6 space-y-4">
            <h2 className="text-xl font-semibold text-foreground mb-4">Informations de l'envoi</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Type d'envoi</p>
                <p className="font-medium text-foreground">{shipment?.shipment_type || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Poids</p>
                <p className="font-medium text-foreground">{shipment?.weight ? `${shipment?.weight} kg` : 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Dimensions</p>
                <p className="font-medium text-foreground">{shipment?.dimensions || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Date de création</p>
                <p className="font-medium text-foreground">{formatDate(shipment?.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Livraison estimée</p>
                <p className="font-medium text-foreground">{formatDate(shipment?.estimated_delivery_date)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Livraison réelle</p>
                <p className="font-medium text-foreground">{formatDate(shipment?.actual_delivery_date)}</p>
              </div>
            </div>
            {shipment?.description && (
              <div className="mt-4">
                <p className="text-sm text-muted-foreground">Description</p>
                <p className="font-medium text-foreground">{shipment?.description}</p>
              </div>
            )}
          </div>

          {/* Sender Information Card */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
              <Icon name="User" size={20} className="mr-2 text-primary" />
              Expéditeur
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Nom</p>
                <p className="font-medium text-foreground">{shipment?.sender_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Téléphone</p>
                <p className="font-medium text-foreground">{shipment?.sender_phone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ville</p>
                <p className="font-medium text-foreground">{shipment?.sender_city || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pays</p>
                <p className="font-medium text-foreground">{shipment?.sender_country || 'N/A'}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm text-muted-foreground">Adresse</p>
                <p className="font-medium text-foreground">{shipment?.sender_address || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Receiver Information Card */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
              <Icon name="MapPin" size={20} className="mr-2 text-primary" />
              Destinataire
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Nom</p>
                <p className="font-medium text-foreground">{shipment?.receiver_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Téléphone</p>
                <p className="font-medium text-foreground">{shipment?.receiver_phone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ville</p>
                <p className="font-medium text-foreground">{shipment?.receiver_city || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pays</p>
                <p className="font-medium text-foreground">{shipment?.receiver_country || 'N/A'}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm text-muted-foreground">Adresse</p>
                <p className="font-medium text-foreground">{shipment?.receiver_address || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Shipment Items */}
          {shipmentItems?.length > 0 && (
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Articles de l'envoi</h2>
              <div className="space-y-2">
                {shipmentItems?.map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                    <div>
                      <p className="font-medium text-foreground">{item?.catalog_items?.name || 'Article'}</p>
                      <p className="text-sm text-muted-foreground">Quantité: {item?.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-foreground">{item?.price_total} €</p>
                      <p className="text-sm text-muted-foreground">{item?.price_unit} € / unité</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Event History Timeline */}
          {events?.length > 0 && (
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
                <Icon name="Clock" size={20} className="mr-2 text-primary" />
                Historique des événements
              </h2>
              <div className="space-y-4">
                {events?.map((event, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                      <Icon name="CheckCircle" size={20} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-foreground">{event?.event_type}</p>
                        <p className="text-sm text-muted-foreground">{formatDate(event?.created_at)}</p>
                      </div>
                      {event?.description && (
                        <p className="text-sm text-muted-foreground mt-1">{event?.description}</p>
                      )}
                      {event?.location && (
                        <p className="text-sm text-muted-foreground mt-1">
                          <Icon name="MapPin" size={14} className="inline mr-1" />
                          {event?.location}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents and Photos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {documents?.length > 0 && (
              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="FileText" size={20} className="mr-2 text-primary" />
                  Documents
                </h2>
                <div className="space-y-2">
                  {documents?.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center">
                        <Icon name="File" size={18} className="mr-2 text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">{doc?.file_name || `Document ${index + 1}`}</span>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Icon name="Download" size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

              {/* ✅ UNIFIED: Display photos ONLY from photos_urls array */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="Image" size={20} className="mr-2 text-primary" />
                  Photos du Colis
                </h2>
                
                {Array.isArray(shipment?.photos_urls) && shipment?.photos_urls?.length > 0 ? (
                  <div className="space-y-4">
                    <p className="text-xs font-medium text-primary mb-2">Photos de collecte</p>
                    <div className="grid grid-cols-2 gap-3">
                      {shipment?.photos_urls?.map((photoUrl, index) => (
                        <div key={index} className="border-2 border-primary/20 rounded-lg overflow-hidden bg-primary/5">
                          <div className="aspect-square bg-muted">
                            <img 
                              src={photoUrl} 
                              alt={`Photo de collecte ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* ✅ DEBUG: Show raw URL data for verification */}
                    <div className="mt-4 p-3 bg-muted/50 rounded border border-border">
                      <p className="text-xs font-semibold text-muted-foreground mb-2">DEBUG - Données brutes :</p>
                      {shipment?.photos_urls?.map((url, idx) => (
                        <p key={idx} className="text-xs font-mono text-foreground break-all mt-1">
                          Photo {idx + 1}: {url}
                        </p>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="text-center py-8 text-muted-foreground">
                      <Icon name="Camera" size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucune photo disponible</p>
                    </div>
                    
                    {/* ✅ DEBUG: Show what data we have */}
                    <div className="p-3 bg-muted/50 rounded border border-border">
                      <p className="text-xs font-semibold text-muted-foreground mb-2">DEBUG - État actuel :</p>
                      <p className="text-xs font-mono text-foreground">
                        photos_urls: {JSON.stringify(shipment?.photos_urls)}
                      </p>
                      <p className="text-xs font-mono text-foreground mt-1">
                        photo_url (ancien): {shipment?.photo_url || 'null'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

          {/* Financial Information */}
          {(shipment?.invoice_url || shipment?.loyalty_points_earned) && (
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Informations financières</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {shipment?.invoice_url && (
                  <div>
                    <p className="text-sm text-muted-foreground">Facture</p>
                    <Button variant="link" className="p-0 h-auto">
                      <Icon name="Download" size={16} className="mr-1" />
                      Télécharger la facture
                    </Button>
                  </div>
                )}
                {shipment?.loyalty_points_earned && (
                  <div>
                    <p className="text-sm text-muted-foreground">Points de fidélité gagnés</p>
                    <p className="font-medium text-foreground">{shipment?.loyalty_points_earned} points</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminShipmentDetailView;